import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Api } from '../../lib/api';
import { XNSError } from "../../errors/XNSError";
import { TestPlan } from './lib/testplan';
import '@ant-design/compatible/assets/index.css';
import { Button, message, Tabs, Modal, Input, Collapse } from 'antd';
import {
    TEST_PLAN_TEMPLATE__SIMPLEX,
    TEST_PLAN_TEMPLATE__DUPLEX,
    TEST_PLAN_TEMPLATES,
    CORE_SIZE__SINGLEMODE,
    POLARITY_TYPE__A,
    POLARITY_TYPE__B,
    POLARITY_TYPE__C,
    ASSEMBLY_TYPE__SINGLE_CABLE,
    ASSEMBLY_LENGTH__LT_100_M,
    RL_MODE__FAST,
    MTJ__SINGLE_END_A,
    MTJ__SINGLE_END_B,
    MTJ__SINGLE_END_A_THEN_END_B,
    MTJ__DOUBLE,
    TJ_TYPE__MEASURE,
    TJ_TYPE__AUTO,
    TJ_TYPE__MANUAL,
    EQUIPMENT_TYPE__RL1,
    EQUIPMENT_TYPE__PT1,
    TEST_PLAN_TEMPLATE__FAN_OUT,
    CORE_SIZE_LABELS,
    ASSEMBLY_TYPE_LABELS,
    MTJ_CONFIGURATION_LABELS,
    TERMINATION_TYPE__FLAT_CONNECTOR,
    REAL_TIME_DISPLAY__FULL,
    REAL_TIME_DISPLAY__FAST,
    RESULTS__FULL,
    SCANNED_SERIAL_NUMBER,
    RETRY_COUNT__NO_LIMIT,
    CABLE_ASSEMBLY_EXPORT__PASS_ONLY
} from '@shared/constants/constants';
import { generateTypeAPolarity, generateTypeBPolarity, generateTypeCPolarity } from '../../lib/cable';
import { generateDefault, generateDefaultDetectorMap } from './lib/defaults';
import { updateRequiredEquipment } from './lib/requiredEquipment';
import Cable from '../../images/cable';
import { triggerValidation, validateWizard, validateDUTConfigPage, validateILRLPage, validateLabelPage } from './lib/validation';

import { DUTConfigPage } from './pages/DUTConfigPage';
import { SetupPage } from './pages/SetupPage';
import { PolarityPage } from './pages/PolarityPage';
import { ILRLPage } from './pages/ILRLPage';
import { TestSettingsPage } from './pages/TestSettingsPage';
import { LabelPage } from './pages/LabelPage';
import { ReviewPage } from './pages/ReviewPage';
import { Prompt } from 'react-router-dom';

import get from 'lodash/get';
import { isEmpty, isEqual } from 'lodash';
import CollapsePanel from 'antd/lib/collapse/CollapsePanel';
import { Equipment, FiberMapEntry, ICustomField, PolarityTestblockProcedureBase, TestBlockProcedureBase, CreateTestPlanDTO, TestPlanTemplates, WizardState } from '@shared/types/test-plan/types.d';
import { buildTestPlanDTO, createWizardData } from './lib/testPlanBuild';

const { TabPane } = Tabs;

const PAGE__DUT_CONFIG = 0;
const PAGE__SETUP = 1;
const PAGE__POLARITY = 2;
const PAGE__IL_RL = 3;
const PAGE__TEST_SETTINGS = 4;
const PAGE__LABEL = 5;
const PAGE__REVIEW = 6;

interface WizardParam {
    id?: string;
    template?: string;
}

interface WizardProps extends RouteComponentProps<WizardParam> {}

class Wizard extends Component<WizardProps, WizardState> {
    constructor(props: WizardProps) {
        super(props);

        this.state = {
            // Wizard options
            page: PAGE__DUT_CONFIG,
            loading: true,
            preventDefaults: false,
            testPlanId: null,

            // TODO: Remove the need for this
            rerenderFiberMap: 0, // Force a re-render of the fiber map

            // Options

            name: '',
            tempName: '',
            tempUrl: '',

            // Assembly (e.g. What is being tested)
            // Contains assembly information independent of how it will be tested
            assembly: {
                coreSize: CORE_SIZE__SINGLEMODE,
                insertionLossSideA: true,
                insertionLossSideB: true,
                insertionLossTotal: false,
                returnLossSideA: true,
                returnLossSideB: true,
                returnLossTotal: false,
                testPolarity: false,
                polaritySequence: 2,
                referenceOrder: 'Channel',
                sideA: {
                    skip: [],
                    il: [],
                    rl: [],
                    rlTotal: []
                },
                sideB: {
                    skip: [],
                    il: [],
                    rl: [],
                    rlTotal: []
                },
                sideTotal: {
                    skip: [],
                    il: [],
                    rl: [],
                    rlTotal: []
                },
                ilTotalList: [],
                ilEndA: null,
                ilEndB: null,
                ilTotal: null,
                rlEndA: null,
                rlEndB: null,
                rlTotal: null,
                terminationTypeEndA: TERMINATION_TYPE__FLAT_CONNECTOR,
                terminationTypeEndB: TERMINATION_TYPE__FLAT_CONNECTOR,
                connectorEndA: null,
                connectorEndB: null,
                polarity: {
                    id: POLARITY_TYPE__A,
                    name: null,
                    mapping: generateTypeAPolarity(1),
                    darkChannels: []
                },
                ilRefLimit: 5,
                ilMinimumLimit: -0.01
            },

            // Equipment (e.g. How it's being tested)
            equipment: {
                singleTestBlock: true,
                // Default MTJ Configuration must match default IL/RL Side A/B checkbox values
                mtjConfiguration: MTJ__SINGLE_END_A_THEN_END_B,
                rlMode: RL_MODE__FAST,
                advanced: true,
                tj1: TJ_TYPE__MEASURE,
                tj2: TJ_TYPE__AUTO,
                fibers: [],
                switch0: [null, null],
                switch1: [null, null],
                switch2: [null, null],
                tj1Length: [],
                tj2Length: [],
                detectors: [1, 1],
                pauseBefore: [],
                pauseMessages: [],
                breakCable: {},
                useSwitch1: false,
                useSwitch2: false,
                showDetectors: false,
                referenceOnlyOnDetector1: false,
                reference: {
                    fibers: [],
                    switch0: [],
                    switch1: [],
                    switch2: [],
                    detectors: [1],
                    pauseBefore: [],
                    pauseMessages: [],
                    skip: []
                },
                fastFail: true,
                ilResolution: 2
            },

            // TODO: These belong under 'assembly'
            wavelength_850: false,
            wavelength_1300: false,
            wavelength_1310: true,
            wavelength_1490: false,
            wavelength_1550: true,
            wavelength_1625: false,
            assemblyType: ASSEMBLY_TYPE__SINGLE_CABLE,
            hybridAssembly: false,
            assemblyLength: ASSEMBLY_LENGTH__LT_100_M,
            fibersPerCable: 1,
            cablesPerAssembly: 1,

            testPlanTemplate: null,

            label: null,
            excelLabel: null,
            secondExcelLabel: null,

            realTimeDisplaySelection: REAL_TIME_DISPLAY__FULL,
            results: RESULTS__FULL,
            allowableRetryCount: RETRY_COUNT__NO_LIMIT,
            retryFailedChannels: false,
            showDutLength: false,
            showCableBreak: false,

            partialResults: false,

            serialNumScheme: SCANNED_SERIAL_NUMBER,
            autoIncrement: false,
            autoPrintBarcode: false,
            cableAssemblyExportResult: CABLE_ASSEMBLY_EXPORT__PASS_ONLY,

            trackDUTCount: false,

            customFields: [],

            allCustomFields: {},
            allLabels: {},
            allExcelLabels: [],

            ilComp: {
                enable: false,
                userInput: false,
                attenuation_850: null,
                attenuation_1300: null,
                attenuation_1310: null,
                attenuation_1490: null,
                attenuation_1550: null,
                attenuation_1625: null
            },

            requiredEquipment: {
                rl1: null,
                pt1: null,
                rds: null,
                switch1: null,
                switch2: null
            },

            validation: {
                // Global
                wizard: true,

                // Pages
                dutConfigPage: true,
                setupPage: true,
                measurementPage: true,
                labelPage: true,
                reviewPage: true,

                fields: {
                    // DUT Config page
                    fibersPerCable: true,
                    cablesPerAssembly: true,
                    terminationTypeEndA: true,
                    terminationTypeEndB: true,

                    // Setup page
                    tests: true,

                    // Measurement page
                    wavelengths: true,
                    switch0: [true, true],
                    switch1: [true, true],
                    switch2: [true, true],
                    tj1Length: [true, true],
                    tj2Length: [true, true],
                    riValid: true,
                    ilCompValid: true,

                    // Label page
                    customField: true,

                    // Review page
                    name: true
                }
            },

            required: {
                tj1Length: false,
                tj2Length: false,
                endA_rlA: false,
                endA_rlB: false,
                endA_rlTotal: false,
                endB_rlA: false,
                endB_rlB: false,
                endB_rlTotal: false
            },
            additionalSpecs: [],
            saveTestPlanClicked: false,

            criticalSettingsChanged: false,
            criticalChanges: [],

            forceOverwrite: false,
            forceCopy: false,
        };

        this.savedTestPlan = null;
    }

    savedTestPlan: any;

    timeouts = {
        name: null,
        wavelengths: null,
        fiberPerCable: null,
        cablesPerAssembly: null,
        switch0: null,
        switch1: null,
        switch2: null
    };

    componentDidMount() {
        // The wizard must be provided with a test plan template to use or a test plan to edit
        const { id, template } = this.props.match.params;

        if(!id && !template) {
            this.props.history.push('/testplans');
            return;
        }

        Api.getCustomFields().then((customFields) => {
            this.setState({
                allCustomFields: customFields.reduce((output, customField) => {
                    output[customField.id] = customField;
                    return output;
                }, {})
            });
        });

        Api.getLabels().then((labels) => {
            let labels_1 = labels;
            if (labels_1[labels_1.length - 1].name === 'DEFAULT_BARCODE_LABEL') {
                labels_1.pop();
            }
            this.setState({
                allLabels: labels_1.reduce((output, label) => {
                    output[label.id] = label;
                    return output;
                }, {})
            });
        });

        Api.getExcelLabels().then((excelLabels) => {
            this.setState({ allExcelLabels: excelLabels });
        });

        // Check if this is a new test plan being created
        if (template) {
            if (template === TEST_PLAN_TEMPLATE__FAN_OUT) {
                this.setState({ testPlanTemplate: template, hybridAssembly: true });
            } else {
                this.setState({ testPlanTemplate: template as TestPlanTemplates });
            }
        }

        // Check if this is an existing test plan being edited
        if (id) {
            this.setState({ testPlanId: Number(id) });

            this.loadTestPlan(id);
        } else {
            this.setState({ loading: false });
        }

        // Set fibersPerCable for Simplex and Duplex templates
        if (template === TEST_PLAN_TEMPLATE__SIMPLEX) {
            this.setState({ fibersPerCable: 1 });
        } else if (template === TEST_PLAN_TEMPLATE__DUPLEX) {
            this.setState({ fibersPerCable: 2 });
        }
    }

    arraysContainDifferingValues(prevArray, currArray) {
        // Check to confirm that arrays are not identical in their contents
        if(prevArray === undefined || prevArray === null || currArray === undefined || currArray === null) {
            return false;
        }
        for(let i = 0; i < prevArray.length; i++) {
            if(prevArray[i] !== currArray[i]) {
                return true;
            }
        }
        return false;
    }

    componentDidUpdate(prevProps, prevState: WizardState) {
        // remake darkChannels array if polarity testing changes
        this.updateDarkChannels(prevState);

        // Update switch0 based on switch1 and switch2 usage
        // if switch 1 is used, set switch0 to 1 where switch1 has a value
        // if switch 2 is used, set switch0 to 2 where switch2 has a value
        this.mapOutputToSwitches(prevState);

        // Resize fibermap arrays
        this.resizeFiberMapArrays(prevState);

        // Update fibers in the reference
        this.dedupeReference(prevState);

        // Update switch0 in the reference
        this.dedupeSwitch0(prevState);

        // Update switch1 in the reference
        this.dedupeSwitch1(prevState);

        this.dedupeSwitch2(prevState);

        // Update detectors in the reference
        this.dedupeDetectors(prevState);

        // Handle reference pauseBefore
        if (
            !this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
                this.state.equipment.singleTestBlock !== prevState.equipment.singleTestBlock ||
                this.state.hybridAssembly !== prevState.hybridAssembly ||
                this.state.equipment.fibers !== prevState.equipment.fibers ||
                this.state.equipment.useSwitch1 !== prevState.equipment.useSwitch1 ||
                this.state.equipment.switch1 !== prevState.equipment.switch1)
        ) {
            this.setState((state) => {
                let referenceMapLength: number | null = null;

                if (
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1
                ) {
                    referenceMapLength = state.cablesPerAssembly;
                } else {
                    referenceMapLength = state.fibersPerCable;
                }

                // If both ends of a hybrid assembly are being tested, a reference for each end is needed
                if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && state.hybridAssembly) {
                    referenceMapLength *= 2;
                }

                // Handle pauses
                let pauseBefore = Array(referenceMapLength).fill(false);
                let pauseMessages = Array(referenceMapLength).fill('');

                // With simplex cables, each fiber is a cable so pauses are needed between each fiber
                if (
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    !state.hybridAssembly
                ) {
                    pauseBefore.fill(true, 1);

                    for (let i = 1; i < pauseMessages.length; i++) {
                        pauseMessages[i] = 'Connect TJ' + (i + 1);
                    }
                }

                // if both sides of a hybrid assembly are being tested, a pause is needed between the two ends
                if (
                    state.testPlanTemplate !== TEST_PLAN_TEMPLATE__FAN_OUT &&
                    state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.equipment.singleTestBlock
                ) {
                    if (
                        state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                        state.cablesPerAssembly > 1 &&
                        state.equipment.useSwitch1
                    ) {
                        pauseBefore.fill(true, 1);

                        for (let i = 1; i < pauseMessages.length; i++) {
                            pauseMessages[i] = 'Connect TJ' + (i + 1);
                        }
                    } else {
                        pauseBefore[state.fibersPerCable] = true;
                        pauseMessages[state.fibersPerCable] = 'Connect TJ2';
                    }
                } else if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) { // if fan-out cables
                    if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
                        pauseBefore = Array(referenceMapLength * state.cablesPerAssembly).fill(false);
                        pauseMessages = Array(referenceMapLength * state.cablesPerAssembly).fill('');
                        // For single cable assemblies, the first half are all invidual cables and the second half is a multifiber cable
                        // so set pauses in between each cable
                        if (state.assemblyType === ASSEMBLY_TYPE__SINGLE_CABLE) {
                            for (let i = 1; i < (referenceMapLength * state.cablesPerAssembly) / 2 + 1; i++) {
                                pauseBefore[i] = true;
                                if ((referenceMapLength * state.cablesPerAssembly) / 2 === i) {
                                    pauseMessages[i] = 'Connect Multifiber TJ 1';
                                } else {
                                    pauseMessages[i] = 'Connect TJ' + (i + 1);
                                }
                            }
                        } else {
                            pauseBefore = Array(referenceMapLength * state.cablesPerAssembly).fill(false);
                            pauseMessages = Array(referenceMapLength * state.cablesPerAssembly).fill('');
                            // Side a is individual cables so pause in between each cable
                            for (let i = 1; i < (referenceMapLength * state.cablesPerAssembly) / 2; i++) {
                                pauseBefore[i] = true;

                                pauseMessages[i] = 'Connect TJ' + (i + 1);
                            }

                            // Side b is a multifiber cable so only one pause is needed for each cable
                            let tjCounter = 1;
                            for (
                                let i = (referenceMapLength * state.cablesPerAssembly) / 2;
                                i < referenceMapLength * state.cablesPerAssembly;
                                i = i + state.fibersPerCable
                            ) {
                                pauseBefore[i] = true;

                                pauseMessages[i] = 'Connect Multifiber TJ' + tjCounter;
                                tjCounter++;
                            }
                        }
                    } else if (state.equipment.mtjConfiguration === MTJ__DOUBLE) {
                        // For double-ended MTJ testing, each cable needs a pause
                        pauseBefore = Array(referenceMapLength * state.cablesPerAssembly).fill(false);
                        pauseMessages = Array(referenceMapLength * state.cablesPerAssembly).fill('');
                        for (let i = 1; i < referenceMapLength * state.cablesPerAssembly; i++) {
                            pauseBefore[i] = true;
                            pauseMessages[i] = 'Connect TJ' + (i + 1);
                        }
                    } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A) {
                        pauseBefore = Array(referenceMapLength * state.cablesPerAssembly).fill(false);
                        pauseMessages = Array(referenceMapLength * state.cablesPerAssembly).fill('');

                        for (let i = 1; i < referenceMapLength * state.cablesPerAssembly; i++) {
                            pauseBefore[i] = true;
                            pauseMessages[i] = 'Connect TJ' + (i + 1);
                        }
                    } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_B) {
                        // For single end B testing, only multifiber cables need pauses
                        pauseBefore = Array(referenceMapLength * state.cablesPerAssembly).fill(false);
                        pauseMessages = Array(referenceMapLength * state.cablesPerAssembly).fill('');

                        if (state.cablesPerAssembly > 1) {
                            let cableCounter = 2;

                            for (
                                let i = state.fibersPerCable;
                                i < referenceMapLength * state.cablesPerAssembly;
                                i = i + state.fibersPerCable
                            ) {
                                pauseBefore[i] = true;

                                pauseMessages[i] = 'Connect Multifiber  TJ' + cableCounter;
                                cableCounter++;
                            }
                        }
                    }
                }
                
                if (
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    !state.hybridAssembly
                ) {
                    let tempDuplicates = [
                        ...new Set(state.equipment.switch1.filter((value, index, self) => self.indexOf(value) !== index)) // find duplicates by checking if indexOf first occurrence is different from current index
                    ];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < state.equipment.switch1.length; j++) {
                            if (tempDuplicates[i] === state.equipment.switch1[j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }
                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        pauseBefore.splice(indexDuplicates[t], 1);
                        pauseMessages.splice(indexDuplicates[t], 1);
                    }
                }

                if (
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    state.hybridAssembly
                ) {
                    let result: (number | null)[][] = [];
                    let tempSwitch1 = state.equipment.switch1;
                    let half = Math.ceil(tempSwitch1.length / 2);

                    result.push(tempSwitch1.slice(0, half));
                    result.push(tempSwitch1.slice(-half));

                    let tempDuplicates = [...new Set(result[0].filter((value, index, self) => self.indexOf(value) !== index))];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[0].length; j++) {
                            if (tempDuplicates[i] === result[0][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }

                    let tempDuplicates2 = [...new Set(result[1].filter((value, index, self) => self.indexOf(value) !== index))];

                    for (let i = 0; i < tempDuplicates2.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[1].length; j++) {
                            if (tempDuplicates2[i] === result[1][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(result[0].length + j);
                                }
                            }
                        }
                    }

                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        pauseBefore.splice(indexDuplicates[t], 1);
                        pauseMessages.splice(indexDuplicates[t], 1);
                    }
                }
                return {
                    equipment: {
                        ...state.equipment,
                        reference: {
                            ...state.equipment.reference,
                            pauseBefore,
                            pauseMessages
                        }
                    }
                };
            });
        }

        // Update skip in the reference
        if (
            !this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
                this.state.hybridAssembly !== prevState.hybridAssembly ||
                this.state.assembly.sideA.skip !== prevState.assembly.sideA.skip ||
                this.state.assembly.sideB.skip !== prevState.assembly.sideB.skip)
        ) {
            this.setState((state) => {
                let skip;

                if (
                    state.equipment.mtjConfiguration === MTJ__SINGLE_END_A ||
                    state.equipment.mtjConfiguration === MTJ__DOUBLE
                ) {
                    skip = state.assembly.sideA.skip.slice(0, state.fibersPerCable);
                } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_B) {
                    skip = state.assembly.sideB.skip.slice(0, state.fibersPerCable);
                } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
                    skip = state.assembly.sideA.skip.slice(0, state.fibersPerCable);

                    if (state.hybridAssembly) {
                        skip = skip.concat(state.assembly.sideB.skip.slice(0, state.fibersPerCable));
                    }
                }

                return {
                    equipment: {
                        ...state.equipment,
                        reference: {
                            ...state.equipment.reference,
                            skip
                        }
                    }
                };
            });
        }
        // Handle all validation of updated fields
        triggerValidation(this.state, prevState, (newState) => this.setState(newState));

        // Update required equipment
        updateRequiredEquipment(this.state, prevState, (newState) => this.setState(newState));

        // If the assembly type is set to a single cable, the cablesPerAssembly must be reset to 1
        if (this.state.assemblyType !== prevState.assemblyType && this.state.assemblyType === ASSEMBLY_TYPE__SINGLE_CABLE) {
            this.setState({ cablesPerAssembly: 1 });
        }

        let returnLossTest =
            this.state.assembly.returnLossSideA || this.state.assembly.returnLossSideB || this.state.assembly.returnLossTotal;
        let prevReturnLossTest =
            prevState.assembly.returnLossSideA || prevState.assembly.returnLossSideB || prevState.assembly.returnLossTotal;

        // Determine whether TJ1 Length column must be displayed
        if (returnLossTest !== prevReturnLossTest || this.state.equipment.tj1 !== prevState.equipment.tj1) {
            this.setState((state) => ({
                required: {
                    ...state.required,
                    tj1Length: returnLossTest && state.equipment.tj1 === TJ_TYPE__MANUAL
                }
            }));
        }

        if (
            returnLossTest !== prevReturnLossTest ||
            this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
            this.state.equipment.tj2 !== prevState.equipment.tj2
        ) {
            this.setState((state) => ({
                required: {
                    ...state.required,
                    tj2Length:
                        returnLossTest &&
                        state.equipment.mtjConfiguration === MTJ__DOUBLE &&
                        state.equipment.tj2 === TJ_TYPE__MANUAL
                }
            }));
        }

        if (
            returnLossTest !== prevReturnLossTest ||
            this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
            this.state.assembly.rlTotal !== prevState.assembly.rlTotal
        ) {
            this.setState((state) => {
                let newState = {
                    required: {
                        ...state.required,
                        endA_rlA: false,
                        endA_rlB: false,
                        endA_rlTotal: false,
                        endB_rlA: false,
                        endB_rlB: false,
                        endB_rlTotal: false
                    }
                };

                if (!returnLossTest) {
                    return newState;
                }

                if (
                    this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_A ||
                    this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B ||
                    this.state.equipment.mtjConfiguration === MTJ__DOUBLE
                ) {
                    newState.required.endA_rlA = true;
                    newState.required.endA_rlTotal = this.state.required.endA_rlTotal;
                }

                if (this.state.equipment.mtjConfiguration === MTJ__DOUBLE) {
                    newState.required.endA_rlB = true;
                }

                if (
                    this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_B ||
                    this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B
                ) {
                    newState.required.endB_rlA = true;
                    newState.required.endB_rlTotal = this.state.required.endB_rlTotal;
                }

                return newState;
            });
        }

        // If TJb is set to Auto, DUT length must be 'Hide'
        if (this.state.equipment.tj2 !== prevState.equipment.tj2 && this.state.equipment.tj2 === TJ_TYPE__AUTO) {
            this.setState({ showDutLength: false });
        }

        // Re-generate default fiber map
        if (
            // Don't immediately overwrite the fiber map when loading a test plan
            !this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                this.state.testPlanTemplate !== prevState.testPlanTemplate ||
                this.state.hybridAssembly !== prevState.hybridAssembly ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
                this.state.equipment.singleTestBlock !== prevState.equipment.singleTestBlock ||
                this.state.assemblyType !== prevState.assemblyType)
        ) {
            let {fibers, switch0, switch1, switch2, pauseBefore, pauseMessages, tj1Length, tj2Length, breakCable} = generateDefault(
                this.state.fibersPerCable,
                this.state.cablesPerAssembly,
                this.state.testPlanTemplate,
                this.state.hybridAssembly,
                this.state.assemblyType,
                this.state.equipment.mtjConfiguration,
                this.state.equipment.singleTestBlock
            );

            this.setState((state) => {
                let newState = {
                    equipment: {
                        ...state.equipment,
                        fibers: fibers,
                        switch0: switch0,
                        switch1: switch1,
                        switch2: switch2,
                        pauseBefore: pauseBefore,
                        pauseMessages: pauseMessages,
                        tj1Length: tj1Length,
                        tj2Length: tj2Length,
                        breakCable: breakCable
                    },
                    rerenderFiberMap: state.rerenderFiberMap + 1
                };

                if (switch1[0] !== null && state.testPlanTemplate !== TEST_PLAN_TEMPLATE__SIMPLEX && !(state.testPlanTemplate === TEST_PLAN_TEMPLATE__DUPLEX && !(state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && state.hybridAssembly))) {
                    newState.equipment.useSwitch1 = true;
                }

                return newState;
            });
        }

        // Check if detector map must be regenerated
        if (
            // Don't immediately overwrite the detector map when loading a test plan
            !this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
                this.state.equipment.showDetectors !== prevState.equipment.showDetectors)
        ) {
            let detectors = generateDefaultDetectorMap(
                this.state.equipment.showDetectors,
                this.state.fibersPerCable,
                this.state.cablesPerAssembly,
                this.state.equipment.mtjConfiguration
            );

            this.setState((state) => ({
                equipment: {
                    ...state.equipment,
                    detectors: detectors
                },
                rerenderFiberMap: state.rerenderFiberMap + 1
            }));
        }
        if (
            (this.state.assembly.insertionLossSideA !== prevState.assembly.insertionLossSideA ||
                this.state.assembly.insertionLossSideB !== prevState.assembly.insertionLossSideB ||
                this.state.assembly.insertionLossTotal !== prevState.assembly.insertionLossTotal) &&
            this.state.assembly.insertionLossSideA === false &&
            this.state.assembly.insertionLossSideB === false &&
            this.state.assembly.insertionLossTotal === false &&
            this.state.ilComp.enable === true
        ) {
            this.setState((state) => ({
                ilComp: {
                    ...state.ilComp,
                    enable: false
                }
            }));
        }

        if (!this.state.preventDefaults && this.state.ilComp.userInput !== prevState.ilComp.userInput) {
            if (this.state.ilComp.enable && this.state.ilComp.userInput) {
                const { id, name } = Object.values(this.state.allCustomFields).find((customTemp) => customTemp.name === 'Length')!;
                
                let lengthCustomField: ICustomField = {
                    id: id,
                    name: name,
                    required: true
                }

                let tempCustomFields = this.state.customFields;

                tempCustomFields.push(lengthCustomField);
                this.setState((state) => ({
                    customFields: tempCustomFields
                }));
            } else {
                let tempCustomFields = this.state.customFields;

                let lengthIndex = this.state.customFields.findIndex((item) => item.name === 'Length');

                if (lengthIndex !== -1) {
                    tempCustomFields.splice(lengthIndex, 1);

                    this.setState((state) => ({
                        customFields: tempCustomFields
                    }));
                }
            }
        }

        //
        // Check if the requirements table needs to be updated
        //
        let fiberCount = this.state.fibersPerCable * this.state.cablesPerAssembly;

        if (this.state.assembly.ilEndA !== prevState.assembly.ilEndA || this.state.assembly.ilEndB !== prevState.assembly.ilEndB) {
            let ilSideAFillValues = Array(fiberCount).fill(this.state.assembly.ilEndA);
            let ilSideBFillValues = Array(fiberCount).fill(this.state.assembly.ilEndB);
            let ilTotal = this.state.assembly.ilTotal;

            if(prevState.assembly.ilEndA === null || prevState.assembly.ilEndA === this.state.assembly.ilEndA) {
                ilSideAFillValues =  Array(fiberCount).fill(null).map((_, index) => this.state.assembly.sideA.il[index] || this.state.assembly.ilEndA);
            }

            if(prevState.assembly.ilEndB === null || prevState.assembly.ilEndB === this.state.assembly.ilEndB) {
                ilSideBFillValues =  Array(fiberCount).fill(null).map((_, index) => this.state.assembly.sideB.il[index] || this.state.assembly.ilEndB);
            }

            if (this.state.equipment.mtjConfiguration === MTJ__DOUBLE && (this.state.assembly.ilEndA !== null && this.state.assembly.ilEndB !== null)) {
                ilTotal = this.state.assembly.ilEndA + this.state.assembly.ilEndB;
            }

            this.setState((state) => ({
                assembly: {
                    ...state.assembly,
                    sideA: {
                        ...state.assembly.sideA,
                        il: ilSideAFillValues
                    },
                    sideB: {
                        ...state.assembly.sideB,
                        il: ilSideBFillValues
                    },
                    ilTotal: ilTotal
                }
            }));
        }

        let rlFillSideTotal: (number | null)[] | null = null;

        if (this.state.assembly.rlEndA !== prevState.assembly.rlEndA || this.state.assembly.rlEndB !== prevState.assembly.rlEndB) {
            let rlTotal = this.state.assembly.rlTotal;
            let rlFillSideA = Array(fiberCount).fill(this.state.assembly.rlEndA);
            let rlFillSideB = Array(fiberCount).fill(this.state.assembly.rlEndB);

            if (this.state.assembly.returnLossTotal && prevState.assembly.rlTotal === this.state.assembly.rlTotal) {
                if ((this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B || this.state.equipment.mtjConfiguration === MTJ__DOUBLE) &&
                    (this.state.assembly.rlEndA !== null && this.state.assembly.rlEndB !== null)) {
                        rlTotal = Math.round(
                            -10 *
                                Math.log10(
                                    Math.pow(10, this.state.assembly.rlEndA / -10) +
                                        Math.pow(10, this.state.assembly.rlEndB / -10)
                                ) *
                                10
                        ) / 10
                } else if( this.state.equipment.mtjConfiguration !== MTJ__SINGLE_END_B && this.state.equipment.mtjConfiguration !== MTJ__SINGLE_END_A && this.state.assembly.rlEndA && this.state.assembly.rlEndB) {
                    rlTotal = Math.round(
                        -10 *
                            Math.log10(
                                Math.pow(10, this.state.assembly.rlEndA / -10) +
                                    Math.pow(10, this.state.assembly.rlEndB / -10)
                            ) *
                            10
                    ) / 10;
                } else if ((this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_A || this.state.assembly.returnLossSideA) && this.state.assembly.rlEndA) {
                    rlTotal = Math.round(this.state.assembly.rlEndA * 10) / 10
                } else if((this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_B || this.state.assembly.returnLossSideB) && this.state.assembly.rlEndB) {
                    rlTotal = Math.round(this.state.assembly.rlEndB * 10) / 10
                }
            }

            if(prevState.assembly.rlEndA === null || prevState.assembly.rlEndA === this.state.assembly.rlEndA) {
                rlFillSideA = Array(fiberCount).fill(null).map((_, index) => this.state.assembly.sideA.rl[index] || this.state.assembly.rlEndA);
            }

            if(prevState.assembly.rlEndB === null || prevState.assembly.rlEndB === this.state.assembly.rlEndB) {
                rlFillSideB = Array(fiberCount).fill(null).map((_, index) => this.state.assembly.sideB.rl[index] || this.state.assembly.rlEndB);
            }

            this.setState((state) => ({
                assembly: {
                    ...state.assembly,
                    sideA: {
                        ...state.assembly.sideA,
                        rl: rlFillSideA
                    },
                    sideB: {
                        ...state.assembly.sideB,
                        rl: rlFillSideB
                    },
                    rlTotal: rlTotal
                }
            }));
        }

        if (this.state.assembly.ilTotal !== prevState.assembly.ilTotal) {
            let ilFillTotal = Array(fiberCount).fill(this.state.assembly.ilTotal);
            if(prevState.assembly.ilTotal === null || prevState.assembly.ilTotal === this.state.assembly.ilTotal) {
                ilFillTotal = Array(fiberCount).fill(null).map((_, index) => this.state.assembly.ilTotalList[index] || this.state.assembly.ilTotal);
            }
            this.setState((state) => ({
                assembly: {
                    ...state.assembly,
                    ilTotalList: ilFillTotal
                }
            }));
        }

        if(prevState.assembly.rlTotal === null || (prevState.assembly.rlTotal === this.state.assembly.rlTotal && this.state.assembly.insertionLossTotal)) {
            rlFillSideTotal = Array(fiberCount).fill(null).map((_, index) => this.state.assembly.sideA.rlTotal[index]);
        } else if(prevState.assembly.rlTotal === null || prevState.assembly.rlTotal === this.state.assembly.rlTotal) {
            rlFillSideTotal = Array(fiberCount).fill(null).map((_, index) => this.state.assembly.sideTotal.rlTotal[index]);
        }

        // Update values if they have changed (avoids updating on load)
        if (this.state.assembly.rlTotal !== prevState.assembly.rlTotal || rlFillSideTotal?.length !== this.state.assembly.sideA.rlTotal.length || this.arraysContainDifferingValues(rlFillSideTotal, prevState.assembly.sideTotal?.rlTotal)) {
            this.setState((state) => ({
                assembly: {
                    ...state.assembly,
                    sideA: {
                        ...state.assembly.sideA,
                        rlTotal: rlFillSideTotal ?? Array(fiberCount).fill(this.state.assembly.rlTotal)
                    },
                    sideB: {
                        ...state.assembly.sideB,
                        rlTotal: rlFillSideTotal ?? Array(fiberCount).fill(this.state.assembly.rlTotal)
                    },
                    sideTotal: {
                        ...state.assembly.sideTotal,
                        rlTotal: rlFillSideTotal ?? Array(fiberCount).fill(this.state.assembly.rlTotal)
                    }
                }
            }));
        }

        // Changes to the number of fibers invalidates the polarity
        if (
            this.state.fibersPerCable !== prevState.fibersPerCable ||
            this.state.cablesPerAssembly !== prevState.cablesPerAssembly
        ) {
            if (this.state.assembly.polarity.id === POLARITY_TYPE__A) {
                this.setState((state) => ({
                    assembly: {
                        ...state.assembly,
                        polarity: {
                            ...state.assembly.polarity,
                            mapping: generateTypeAPolarity(state.fibersPerCable * state.cablesPerAssembly)
                        }
                    }
                }));
            } else if (this.state.assembly.polarity.id === POLARITY_TYPE__B) {
                this.setState((state) => ({
                    assembly: {
                        ...state.assembly,
                        polarity: {
                            ...state.assembly.polarity,
                            mapping: generateTypeBPolarity(state.fibersPerCable * state.cablesPerAssembly)
                        }
                    }
                }));
            } else if (this.state.assembly.polarity.id === POLARITY_TYPE__C) {
                this.setState((state) => ({
                    assembly: {
                        ...state.assembly,
                        polarity: {
                            ...state.assembly.polarity,
                            mapping: generateTypeCPolarity(state.fibersPerCable * state.cablesPerAssembly)
                        }
                    }
                }));
            } else if (!this.state.preventDefaults) {
                this.setState((state) => ({
                    assembly: {
                        ...state.assembly,
                        polarity: {
                            id: POLARITY_TYPE__A,
                            name: null,
                            mapping: generateTypeAPolarity(state.fibersPerCable * state.cablesPerAssembly)
                        }
                    }
                }));

                message.info('Polarity reset to default because the fiber count changed.', 10);
            }
        }

        if (
            !this.state.preventDefaults &&
            (this.state.assembly.insertionLossSideA !== prevState.assembly.insertionLossSideA ||
                this.state.assembly.insertionLossSideB !== prevState.assembly.insertionLossSideB ||
                this.state.assembly.returnLossSideA !== prevState.assembly.returnLossSideA ||
                this.state.assembly.returnLossSideB !== prevState.assembly.returnLossSideB ||
                this.state.assembly.returnLossTotal !== prevState.assembly.returnLossTotal ||
                this.state.assembly.insertionLossTotal !== prevState.assembly.insertionLossTotal ||
                this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration)
        ) {
            let totalFibers = this.state.fibersPerCable * this.state.cablesPerAssembly;
            let sideAil: (number | null)[] = [];
            let sideArl: (number | null)[] = [];
            let sideBil: (number | null)[] = [];
            let sideBrl: (number | null)[] = [];
            let sideTotalrl: (number | null)[] = [];
            for(let i = 0; i < totalFibers; i++) {
                sideAil[i] = prevState.assembly.sideA.il[i] ?? this.state.assembly.ilEndA;
                sideArl[i] = prevState.assembly.sideA.rl[i] ?? this.state.assembly.rlEndA;
                sideBil[i] = prevState.assembly.sideB.il[i] ?? this.state.assembly.ilEndB;
                sideBrl[i] = prevState.assembly.sideB.rl[i] ?? this.state.assembly.rlEndB;

                if(prevState.assembly.sideTotal !== undefined && prevState.assembly.sideTotal.rl !== undefined) {
                    sideTotalrl[i] = prevState.assembly.sideTotal?.rl[i] ?? this.state.assembly.rlTotal;
                }
            }
            if (
                this.state.showDutLength &&
                !this.state.assembly.returnLossSideA &&
                !this.state.assembly.returnLossSideB &&
                !this.state.assembly.returnLossTotal
            ) {
                this.setState((state) => ({
                    showDutLength: false
                }));
            }
            this.setState((state) => ({
                assembly: {
                    ...state.assembly,
                    sideA: {
                        ...state.assembly.sideA,
                        il: sideAil,
                        rl: sideArl
                    },
                    sideB: {
                        ...state.assembly.sideB,
                        il: sideBil,
                        rl: sideBrl
                    },
                    sideTotal: {
                        ...state.assembly.sideTotal,
                        sideTotal: sideTotalrl
                    }
                }
            }));
        }

        // Validate on page change
        if (this.state.page !== prevState.page) {
            if (this.state.page === PAGE__REVIEW) {
                validateWizard((newState) => this.setState(newState));
            } else if (prevState.page === PAGE__DUT_CONFIG) {
                validateDUTConfigPage((newState) => this.setState(newState));
            } else if (prevState.page === PAGE__IL_RL) {
                validateILRLPage((newState) => this.setState(newState));
            } else if (prevState.page === PAGE__LABEL) {
                validateLabelPage((newState) => this.setState(newState));
            }
        }
    }

    private dedupeDetectors(prevState: WizardState) {
        if (!this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
                this.state.hybridAssembly !== prevState.hybridAssembly ||
                this.state.equipment.detectors !== prevState.equipment.detectors ||
                this.state.equipment.useSwitch1 !== prevState.equipment.useSwitch1 ||
                this.state.equipment.switch1 !== prevState.equipment.switch1 ||
                this.state.equipment.referenceOnlyOnDetector1 !== prevState.equipment.referenceOnlyOnDetector1)) {
            this.setState((state) => {
                // detectors values for the fibers in the first cable
                let detectors: (number | null)[] = [];
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1) {
                    detectors = state.equipment.referenceOnlyOnDetector1 ? Array(state.cablesPerAssembly).fill(1) : state.equipment.detectors.slice(0, state.cablesPerAssembly);
                } else {
                    detectors = state.equipment.referenceOnlyOnDetector1 ? Array(state.fibersPerCable).fill(1) : state.equipment.detectors.slice(0, state.fibersPerCable);
                }
                
                // If both ends of a hybrid assembly are being tested, add the detectors values for the first cable of end B
                if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate !== TEST_PLAN_TEMPLATE__FAN_OUT) {
                    if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                        state.cablesPerAssembly > 1 &&
                        state.equipment.useSwitch1) {
                        detectors = state.equipment.referenceOnlyOnDetector1 ?
                            detectors.concat(Array(state.cablesPerAssembly).fill(1)) :
                            detectors.concat(
                                state.equipment.detectors.slice(
                                    state.equipment.detectors.length / 2,
                                    state.equipment.detectors.length / 2 + state.cablesPerAssembly
                                )
                            );
                    } else {
                        detectors = state.equipment.referenceOnlyOnDetector1 ?
                            detectors.concat(Array(state.fibersPerCable).fill(1)) :
                            detectors.concat(
                                state.equipment.detectors.slice(
                                    state.equipment.detectors.length / 2,
                                    state.equipment.detectors.length / 2 + state.fibersPerCable
                                )
                            );
                    }
                } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    detectors = state.equipment.referenceOnlyOnDetector1 ? Array(state.equipment.detectors.length).fill(1) : state.equipment.detectors;
                } else if (state.equipment.mtjConfiguration === MTJ__DOUBLE &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    detectors = state.equipment.referenceOnlyOnDetector1 ? Array(state.equipment.detectors.length).fill(1) : state.equipment.detectors;
                } else if ((state.equipment.mtjConfiguration === MTJ__SINGLE_END_A ||
                    state.equipment.mtjConfiguration === MTJ__SINGLE_END_B) &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    detectors = state.equipment.referenceOnlyOnDetector1 ? Array(state.equipment.detectors.length).fill(1) : state.equipment.detectors;
                }
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    !state.hybridAssembly) {
                    let tempDuplicates = [
                        ...new Set(state.equipment.switch1.filter((value, index, self) => self.indexOf(value) !== index))
                    ];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < state.equipment.switch1.length; j++) {
                            if (tempDuplicates[i] === state.equipment.switch1[j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }
                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        detectors.splice(indexDuplicates[t], 1);
                    }
                }
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    state.hybridAssembly) {
                    let result: (number | null)[][] = [];
                    let tempSwitch1 = state.equipment.switch1;
                    let half = Math.ceil(tempSwitch1.length / 2);

                    result.push(tempSwitch1.slice(0, half));
                    result.push(tempSwitch1.slice(-half));

                    let tempDuplicates = [...new Set(result[0].filter((value, index, self) => self.indexOf(value) !== index))];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[0].length; j++) {
                            if (tempDuplicates[i] === result[0][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }

                    let tempDuplicates2 = [...new Set(result[1].filter((value, index, self) => self.indexOf(value) !== index))];

                    for (let i = 0; i < tempDuplicates2.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[1].length; j++) {
                            if (tempDuplicates2[i] === result[1][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(result[0].length + j);
                                }
                            }
                        }
                    }

                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        detectors.splice(indexDuplicates[t], 1);
                    }
                }

                return {
                    equipment: {
                        ...state.equipment,
                        reference: {
                            ...state.equipment.reference,
                            detectors
                        }
                    }
                };
            });
        }
    }

    private dedupeSwitch2(prevState: WizardState) {
        if (!this.state.preventDefaults &&
            (this.state.equipment.switch2 !== prevState.equipment.switch2 ||
                this.state.equipment.useSwitch2 !== prevState.equipment.useSwitch2)) {
            this.setState((state) => {
                // switch2 values for the fibers in the first cable
                let switch2: (number | null)[] = [];
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch2) {
                    switch2 = state.equipment.switch2.slice(0, state.cablesPerAssembly);
                } else {
                    switch2 = state.equipment.switch2.slice(0, state.fibersPerCable);
                }

                if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate !== TEST_PLAN_TEMPLATE__FAN_OUT) {
                    if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                        state.cablesPerAssembly > 1 &&
                        state.equipment.useSwitch2) {
                        switch2 = switch2.concat(
                            state.equipment.switch2.slice(
                                state.equipment.switch2.length / 2,
                                state.equipment.switch2.length / 2 + state.cablesPerAssembly
                            )
                        );
                    } else {
                        switch2 = switch2.concat(
                            state.equipment.switch2.slice(
                                state.equipment.switch2.length / 2,
                                state.equipment.switch2.length / 2 + state.fibersPerCable
                            )
                        );
                    }
                } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch2 = state.equipment.switch2;
                } else if (state.equipment.mtjConfiguration === MTJ__DOUBLE &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch2 = state.equipment.switch2;
                } else if ((state.equipment.mtjConfiguration === MTJ__SINGLE_END_A ||
                    state.equipment.mtjConfiguration === MTJ__SINGLE_END_B) &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch2 = state.equipment.switch2;
                }

                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch2 &&
                    !state.hybridAssembly) {
                    let tempDuplicates = [
                        ...new Set(state.equipment.switch2.filter((value, index, self) => self.indexOf(value) !== index))
                    ];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < state.equipment.switch2.length; j++) {
                            if (tempDuplicates[i] === state.equipment.switch2[j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }
                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        switch2.splice(indexDuplicates[t], 1);
                    }
                }
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch2 &&
                    state.hybridAssembly) {
                    let result: (number | null)[][] = [];
                    let tempSwitch2 = state.equipment.switch2;
                    let half = Math.ceil(tempSwitch2.length / 2);

                    result.push(tempSwitch2.slice(0, half));
                    result.push(tempSwitch2.slice(-half));

                    let tempDuplicates = [...new Set(result[0].filter((value, index, self) => self.indexOf(value) !== index))];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[0].length; j++) {
                            if (tempDuplicates[i] === result[0][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }

                    let tempDuplicates2 = [...new Set(result[1].filter((value, index, self) => self.indexOf(value) !== index))];

                    for (let i = 0; i < tempDuplicates2.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[1].length; j++) {
                            if (tempDuplicates2[i] === result[1][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(result[0].length + j);
                                }
                            }
                        }
                    }

                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        switch2.splice(indexDuplicates[t], 1);
                    }
                }

                return {
                    equipment: {
                        ...state.equipment,
                        reference: {
                            ...state.equipment.reference,
                            switch2
                        }
                    }
                };
            });
        }
    }

    private dedupeSwitch1(prevState: WizardState) {
        if (!this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
                this.state.hybridAssembly !== prevState.hybridAssembly ||
                this.state.equipment.switch1 !== prevState.equipment.switch1 ||
                this.state.equipment.useSwitch1 !== prevState.equipment.useSwitch1)) {
            this.setState((state) => {
                // switch1 values for the fibers in the first cable
                let switch1: (number | null)[] = [];
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1) {
                    switch1 = state.equipment.switch1.slice(0, state.cablesPerAssembly);
                } else {
                    switch1 = state.equipment.switch1.slice(0, state.fibersPerCable);
                }
                
                // If both ends of a hybrid assembly are being tested, add the switch1 values for the first cable of end B
                if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate !== TEST_PLAN_TEMPLATE__FAN_OUT) {
                    if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                        state.cablesPerAssembly > 1 &&
                        state.equipment.useSwitch1) {
                        switch1 = switch1.concat(
                            state.equipment.switch1.slice(
                                state.equipment.switch1.length / 2,
                                state.equipment.switch1.length / 2 + state.cablesPerAssembly
                            )
                        );
                    } else {
                        switch1 = switch1.concat(
                            state.equipment.switch1.slice(
                                state.equipment.switch1.length / 2,
                                state.equipment.switch1.length / 2 + state.fibersPerCable
                            )
                        );
                    }
                } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch1 = state.equipment.switch1;
                } else if (state.equipment.mtjConfiguration === MTJ__DOUBLE &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch1 = state.equipment.switch1;
                } else if ((state.equipment.mtjConfiguration === MTJ__SINGLE_END_A ||
                    state.equipment.mtjConfiguration === MTJ__SINGLE_END_B) &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch1 = state.equipment.switch1;
                }

                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    !state.hybridAssembly) {
                    let tempDuplicates = [
                        ...new Set(state.equipment.switch1.filter((value, index, self) => self.indexOf(value) !== index))
                    ];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < state.equipment.switch1.length; j++) {
                            if (tempDuplicates[i] === state.equipment.switch1[j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }
                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        switch1.splice(indexDuplicates[t], 1);
                    }
                }
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    state.hybridAssembly) {
                    let result: (number | null)[][] = [];
                    let tempSwitch1 = state.equipment.switch1;
                    let half = Math.ceil(tempSwitch1.length / 2);

                    result.push(tempSwitch1.slice(0, half));
                    result.push(tempSwitch1.slice(-half));

                    let tempDuplicates = [...new Set(result[0].filter((value, index, self) => self.indexOf(value) !== index))];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[0].length; j++) {
                            if (tempDuplicates[i] === result[0][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }

                    let tempDuplicates2 = [...new Set(result[1].filter((value, index, self) => self.indexOf(value) !== index))];

                    for (let i = 0; i < tempDuplicates2.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[1].length; j++) {
                            if (tempDuplicates2[i] === result[1][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(result[0].length + j);
                                }
                            }
                        }
                    }

                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        switch1.splice(indexDuplicates[t], 1);
                    }
                }

                return {
                    equipment: {
                        ...state.equipment,
                        reference: {
                            ...state.equipment.reference,
                            switch1
                        }
                    }
                };
            });
        }
    }

    private dedupeSwitch0(prevState: WizardState) {
        if (!this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
                this.state.hybridAssembly !== prevState.hybridAssembly ||
                this.state.equipment.switch0 !== prevState.equipment.switch0 ||
                this.state.equipment.useSwitch1 !== prevState.equipment.useSwitch1 ||
                this.state.equipment.useSwitch2 !== prevState.equipment.useSwitch2)) {
            this.setState((state) => {
                // switch0 values for the fibers in the first cable
                let switch0: (number | null)[] = [];
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1) {
                    switch0 = state.equipment.switch0.slice(0, state.cablesPerAssembly);
                } else {
                    switch0 = state.equipment.switch0.slice(0, state.fibersPerCable);
                }
                
                // If both ends of a hybrid assembly are being tested, add the switch0 values for the first cable of end B
                if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate !== TEST_PLAN_TEMPLATE__FAN_OUT) {
                    if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                        state.cablesPerAssembly > 1 &&
                        state.equipment.useSwitch1) {
                        switch0 = switch0.concat(
                            state.equipment.switch0.slice(
                                state.equipment.switch0.length / 2,
                                state.equipment.switch0.length / 2 + state.cablesPerAssembly
                            )
                        );
                    } else {
                        switch0 = switch0.concat(
                            state.equipment.switch0.slice(
                                state.equipment.switch0.length / 2,
                                state.equipment.switch0.length / 2 + state.fibersPerCable
                            )
                        );
                    }
                } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch0 = state.equipment.switch0;
                } else if (state.equipment.mtjConfiguration === MTJ__DOUBLE &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch0 = state.equipment.switch0;
                } else if ((state.equipment.mtjConfiguration === MTJ__SINGLE_END_A ||
                    state.equipment.mtjConfiguration === MTJ__SINGLE_END_B) &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    switch0 = state.equipment.switch0;
                }

                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    !state.hybridAssembly) {
                    let tempDuplicates = [
                        ...new Set(state.equipment.switch1.filter((value, index, self) => self.indexOf(value) !== index))
                    ];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < state.equipment.switch1.length; j++) {
                            if (tempDuplicates[i] === state.equipment.switch1[j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }
                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        switch0.splice(indexDuplicates[t], 1);
                    }
                }
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    state.hybridAssembly) {
                    let result: (number | null)[][] = [];
                    let tempSwitch1 = state.equipment.switch1;
                    let half = Math.ceil(tempSwitch1.length / 2);

                    result.push(tempSwitch1.slice(0, half));
                    result.push(tempSwitch1.slice(-half));

                    let tempDuplicates = [...new Set(result[0].filter((value, index, self) => self.indexOf(value) !== index))];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[0].length; j++) {
                            if (tempDuplicates[i] === result[0][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }

                    let tempDuplicates2 = [...new Set(result[1].filter((value, index, self) => self.indexOf(value) !== index))];

                    for (let i = 0; i < tempDuplicates2.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[1].length; j++) {
                            if (tempDuplicates2[i] === result[1][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(result[0].length + j);
                                }
                            }
                        }
                    }

                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        switch0.splice(indexDuplicates[t], 1);
                    }
                }
                return {
                    equipment: {
                        ...state.equipment,
                        reference: {
                            ...state.equipment.reference,
                            switch0
                        }
                    }
                };
            });
        }
    }

    private dedupeReference(prevState: WizardState) {
        if (!this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration ||
                this.state.hybridAssembly !== prevState.hybridAssembly ||
                this.state.equipment.fibers !== prevState.equipment.fibers ||
                this.state.equipment.useSwitch1 !== prevState.equipment.useSwitch1 ||
                this.state.equipment.switch1 !== prevState.equipment.switch1)) {
            this.setState((state) => {
                // Names for the fibers in the first cable
                let fibers: string[] = [];
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1) {
                    fibers = state.equipment.fibers.slice(0, state.cablesPerAssembly);
                } else {
                    fibers = state.equipment.fibers.slice(0, state.fibersPerCable);
                }

                // If both ends of a hybrid assembly are being tested, add the names for the first cable of end B
                if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate !== TEST_PLAN_TEMPLATE__FAN_OUT) {
                    if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                        state.cablesPerAssembly > 1 &&
                        state.equipment.useSwitch1) {
                        fibers = fibers.concat(
                            state.equipment.fibers.slice(
                                state.equipment.fibers.length / 2,
                                state.equipment.fibers.length / 2 + state.cablesPerAssembly
                            )
                        );
                    } else {
                        fibers = fibers.concat(
                            state.equipment.fibers.slice(
                                state.equipment.fibers.length / 2,
                                state.equipment.fibers.length / 2 + state.fibersPerCable
                            )
                        );
                    }
                } else if (state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    fibers = state.equipment.fibers;
                } else if (state.equipment.mtjConfiguration === MTJ__DOUBLE &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    fibers = state.equipment.fibers;
                } else if ((state.equipment.mtjConfiguration === MTJ__SINGLE_END_A ||
                    state.equipment.mtjConfiguration === MTJ__SINGLE_END_B) &&
                    state.hybridAssembly &&
                    state.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) {
                    fibers = state.equipment.fibers;
                }

                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    !state.hybridAssembly) {
                    let tempDuplicates = [
                        ...new Set(state.equipment.switch1.filter((value, index, self) => self.indexOf(value) !== index))
                    ];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < state.equipment.switch1.length; j++) {
                            if (tempDuplicates[i] === state.equipment.switch1[j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }
                    
                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        fibers.splice(indexDuplicates[t], 1);
                    }
                }
                if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX &&
                    state.cablesPerAssembly > 1 &&
                    state.equipment.useSwitch1 &&
                    state.hybridAssembly) {
                    let result: (number | null)[][] = [];
                    let tempSwitch1 = state.equipment.switch1;
                    let half = Math.ceil(tempSwitch1.length / 2);

                    result.push(tempSwitch1.slice(0, half));
                    result.push(tempSwitch1.slice(-half));

                    let tempDuplicates = [...new Set(result[0].filter((value, index, self) => self.indexOf(value) !== index))];
                    let indexDuplicates: number[] = [];

                    for (let i = 0; i < tempDuplicates.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[0].length; j++) {
                            if (tempDuplicates[i] === result[0][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(j);
                                }
                            }
                        }
                    }

                    let tempDuplicates2 = [...new Set(result[1].filter((value, index, self) => self.indexOf(value) !== index))];

                    for (let i = 0; i < tempDuplicates2.length; i++) {
                        let counter = 0;

                        for (let j = 0; j < result[1].length; j++) {
                            if (tempDuplicates2[i] === result[1][j]) {
                                counter++;
                                if (counter > 1) {
                                    indexDuplicates.push(result[0].length + j);
                                }
                            }
                        }
                    }

                    for (let t = indexDuplicates.length - 1; t >= 0; t--) {
                        fibers.splice(indexDuplicates[t], 1);
                    }
                }

                return {
                    equipment: {
                        ...state.equipment,
                        reference: {
                            ...state.equipment.reference,
                            fibers
                        }
                    }
                };
            });
        }
    }

    private resizeFiberMapArrays(prevState: WizardState) {
        if (!this.state.preventDefaults &&
            (this.state.fibersPerCable !== prevState.fibersPerCable ||
                this.state.cablesPerAssembly !== prevState.cablesPerAssembly ||
                (this.state.equipment.mtjConfiguration !== prevState.equipment.mtjConfiguration &&
                    (this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B ||
                        prevState.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B)) ||
                this.state.equipment.singleTestBlock !== prevState.equipment.singleTestBlock)) {
            let fiberMapLength = this.state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                this.state.hybridAssembly &&
                this.state.equipment.singleTestBlock
                ? this.state.fibersPerCable * this.state.cablesPerAssembly * 2
                : this.state.fibersPerCable * this.state.cablesPerAssembly;
            let prevFiberMapLength = prevState.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B &&
                this.state.hybridAssembly &&
                this.state.equipment.singleTestBlock
                ? prevState.fibersPerCable * prevState.cablesPerAssembly * 2
                : prevState.fibersPerCable * prevState.cablesPerAssembly;

            this.setState((state) => {
                let newState = {
                    equipment: {
                        ...state.equipment,
                        switch0: [...state.equipment.switch0],
                        switch1: [...state.equipment.switch1],
                        switch2: [...state.equipment.switch2],
                        tj1Length: [...state.equipment.tj1Length],
                        tj2Length: [...state.equipment.tj2Length]
                    },
                    validation: {
                        ...state.validation,
                        fields: {
                            ...state.validation.fields,
                            switch0: [...state.validation.fields.switch0],
                            switch1: [...state.validation.fields.switch1],
                            switch2: [...state.validation.fields.switch2],
                            tj1Length: [...state.validation.fields.tj1Length],
                            tj2Length: [...state.validation.fields.tj2Length]
                        }
                    }
                };

                newState.equipment.switch0.length = fiberMapLength;
                newState.equipment.switch1.length = fiberMapLength;
                newState.equipment.switch2.length = fiberMapLength;
                newState.equipment.tj1Length.length = fiberMapLength;
                newState.equipment.tj2Length.length = fiberMapLength;

                newState.validation.fields.switch0.length = fiberMapLength;
                newState.validation.fields.switch1.length = fiberMapLength;
                newState.validation.fields.switch2.length = fiberMapLength;
                newState.validation.fields.tj1Length.length = fiberMapLength;
                newState.validation.fields.tj2Length.length = fiberMapLength;

                newState.equipment.switch0.fill(null, prevFiberMapLength);
                newState.equipment.switch1.fill(null, prevFiberMapLength);
                newState.equipment.switch2.fill(null, prevFiberMapLength);
                newState.equipment.tj1Length.fill(null, prevFiberMapLength);
                newState.equipment.tj2Length.fill(null, prevFiberMapLength);

                newState.validation.fields.switch0.fill(true, prevFiberMapLength);
                newState.validation.fields.switch1.fill(true, prevFiberMapLength);
                newState.validation.fields.switch2.fill(true, prevFiberMapLength);
                newState.validation.fields.tj1Length.fill(true, prevFiberMapLength);
                newState.validation.fields.tj2Length.fill(true, prevFiberMapLength);

                return newState;
            });
        }
    }

    private mapOutputToSwitches(prevState: WizardState) {
        if (!this.state.preventDefaults && (
            this.state.equipment.useSwitch1 !== prevState.equipment.useSwitch1 ||
            this.state.equipment.useSwitch2 !== prevState.equipment.useSwitch2
        )) {
            if (this.state.equipment.useSwitch1 && this.state.equipment.useSwitch2) {
                this.setState((state) => {
                    let switch0 = state.equipment.switch0;

                    for (let i = 0; i < state.equipment.switch1.length; i += 1) {
                        if ( // @ts-ignore
                            (state.equipment.switch1[i] === null || state.equipment.switch1[i] === '') && // @ts-ignore
                            (state.equipment.switch2[i] !== null && state.equipment.switch2[i] !== '')) {
                            switch0[i] = 2;
                        } else {
                            switch0[i] = 1;
                        }
                    }

                    return {
                        equipment: {
                            ...state.equipment,
                            switch0
                        }
                    };
                });
            }

            if (this.state.equipment.useSwitch1 && this.state.equipment.useSwitch2 === false) {
                this.setState((state) => {
                    let switch0 = state.equipment.switch0;
                    switch0.fill(1);
                    return {
                        equipment: {
                            ...state.equipment,
                            switch0
                        }
                    };
                });
            }

            if (this.state.equipment.useSwitch1 === false && this.state.equipment.useSwitch2) {
                this.setState((state) => {
                    let switch0 = state.equipment.switch0;
                    switch0.fill(2);
                    return {
                        equipment: {
                            ...state.equipment,
                            switch0
                        }
                    };
                });
            }
            if (this.state.equipment.useSwitch1 === false && this.state.equipment.useSwitch2 === false) {
                this.setState((state) => {
                    let switch0 = state.equipment.switch0;

                    if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__DUPLEX && !(state.equipment.mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && state.hybridAssembly)) {
                        for (let i = 0; i < switch0.length; i++) {
                            if (i % 2 === 0) {
                                switch0[i] = 1;
                            } else {
                                switch0[i] = 2;
                            }
                        }
                    }
                    else if (state.testPlanTemplate === TEST_PLAN_TEMPLATE__SIMPLEX && state.hybridAssembly) {
                        switch0[0] = 1;
                        switch0[1] = 2;
                    }
                    else {
                        switch0.fill(1);
                    }
                    return {
                        equipment: {
                            ...state.equipment,
                            switch0
                        }
                    };
                });
            }
        }
    }

    private updateDarkChannels(prevState: WizardState) {
        if (this.state.assembly.testPolarity !== prevState.assembly.testPolarity) {
            let darkChannels: number[] = [];
            for (let i = 0; i < this.state.assembly.polarity.mapping.length; i++) {
                if (this.state.assembly.polarity.mapping[i] === 0) {
                    darkChannels.push(i);
                }
            }
            this.setState((state) => {
                return {
                    assembly: {
                        ...state.assembly,
                        polarity: {
                            ...state.assembly.polarity,
                            darkChannels
                        }
                    }
                };
            });
        }
    }

    async saveTestPlan() {
        this.setState({saveTestPlanClicked: true});

        const wizardData = createWizardData(this.state);
        
        const testPlan = await buildTestPlanDTO(this.state.name!, this.state.testPlanTemplate!, wizardData, this.state.required);
        
        // if not new test plan, check for critical changes
        if(!this.props.location.pathname.includes('/new/')) {
            if(!this.savedTestPlan || isEmpty(this.savedTestPlan)) {
                this.savedTestPlan = await Api.getTestPlan(this.state.testPlanId);
            }

            const oldCriticalChanges = this.state.criticalChanges;

            const criticalSettings = [
                { path: "wizardData.assembly.coreSize", title: "Core Size", formatter: (value) => CORE_SIZE_LABELS[value] || value },
                { path: "wizardData.hybridAssembly", title: "Hybrid Assembly"},
                { path: "wizardData.assemblyType", title: "Assembly Type", formatter: (value) => ASSEMBLY_TYPE_LABELS[value] || value },
                { path: "wizardData.cablesPerAssembly", title: "Cables Per Assembly"},
                { path: "wizardData.fibersPerCable", title: "Fibers Per Cable"},
                { path: "wizardData.equipment.mtjConfiguration", title: "MTJ Configuration", formatter: (value) => MTJ_CONFIGURATION_LABELS[value] || value }
            ];
            
            const criticalChanges = criticalSettings.map(setting => {
                let original = get(this.savedTestPlan, setting.path);
                let current = get(testPlan, setting.path);

                if(!isEqual(current, original)) {
                    return {
                        setting: setting.title,
                        original: setting.formatter ? setting.formatter(original) : original.toString(),
                        current: setting.formatter ? setting.formatter(current) : current.toString()
                    };
                }

                return null;
            }).filter(change => change !== null);

            const criticalSettingsChanged = criticalChanges.length > 0;

            const hasNewCriticalChanges = !isEqual(oldCriticalChanges, criticalChanges);
            const hasUnsavedCriticalChanges = criticalSettingsChanged && !(this.state.forceCopy || this.state.forceOverwrite);

            if(hasNewCriticalChanges || hasUnsavedCriticalChanges) {
                let tempName = this.state.tempName ? this.state.tempName : `${this.state.name} - modified`

                let i = 1;
                while(await this.checkTestPlanName(tempName)) {
                    tempName = `${this.state.name} - modified ${i}`;
                    i++;
                }

                this.setState({
                    criticalSettingsChanged: criticalSettingsChanged,
                    criticalChanges: criticalChanges,
                    tempName: tempName,
                });

                return;
            }
        }
        
        // Update existing test plan
        // update only change if not forced to create new test plan
        if (this.state.testPlanId && !this.state.forceCopy) {
            try {
                await TestPlan.update(this.savedTestPlan.uuid, testPlan);
            } catch (e) {
                if (!(e instanceof XNSError)) {
                    throw e;
                }

                message.error(`Failed to update test plan: ${e.toString()}`);
                console.error(e);
                return;
            }
        } else {
            // Save new test plan
            let nameTaken = await this.checkTestPlanName();

            if (nameTaken) {
                this.setState({ forceCopy: false, forceOverwrite: false });

                alert('Test plan name already exists ');
                return;
            }

            testPlan.testPlanTemplateId = TEST_PLAN_TEMPLATES.find(
                (template) => template.name === this.state.testPlanTemplate
            )?.id ?? null;

            try {
                await TestPlan.create(testPlan);
            } catch (e) {
                this.setState({ saveTestPlanClicked: false, forceCopy: false, forceOverwrite: false });

                if (!(e instanceof XNSError)) {
                    throw e;
                }

                message.error(`Failed to create test plan: ${e.toString()}`);
                console.error(e);
                return;
            }
        }

        // Return to list of test plans
        this.props.history.push('/testplans');
    }

    loadTestPlan(testPlanId) {
        // Retrieve the test plan
        Api.getTestPlan(testPlanId).then((testPlan) => {
            // The test plan must have a testPlanTemplateId to be editable in the wizard
            if (!('testPlanTemplateId' in testPlan) || testPlan.testPlanTemplateId === null) {
                this.props.history.push('/testplans');
                return;
            }

            // TODO: Remove the need for this
            this.savedTestPlan = testPlan;

            // When changes are made to some fields, the fiber map and the detector map are regenerated to reflect the changes.
            // Loading a test plan triggers this regeneration as well. To prevent this, set a flag (preventDefaults) while the loaded test plan is written.

            let newState = {
                ...this.state,
                preventDefaults: true,
                loading: false,
                testPlanTemplate: TEST_PLAN_TEMPLATES.find((template) => template.id === testPlan.testPlanTemplateId)?.name as TestPlanTemplates,
            };

            if ('wizardData' in testPlan && typeof testPlan.wizardData === 'object' && testPlan.wizardData !== null) {
                newState.name = testPlan.name;
                for (let element of [
                    'assembly',
                    'wavelength_850',
                    'wavelength_1300',
                    'wavelength_1310',
                    'wavelength_1490',
                    'wavelength_1550',
                    'wavelength_1625',
                    'assemblyType',
                    'hybridAssembly',
                    'assemblyLength',
                    'fibersPerCable',
                    'cablesPerAssembly',

                    'equipment',

                    'realTimeDisplaySelection',
                    'results',
                    'allowableRetryCount',
                    'retryFailedChannels',
                    'showDutLength',

                    'serialNumScheme',
                    'autoPrintBarcode',
                    'trackDUTCount',
                    'autoIncrement',
                    'cableAssemblyExportResult',
                    'partialResults',

                    'customFields',
                    'label',
                    'excelLabel',
                    'secondExcelLabel',
                    'additionalSpecs',
                    'ilComp'
                ]) {
                    if (
                        element in testPlan.wizardData &&
                        typeof testPlan.wizardData[element] !== 'undefined' &&
                        testPlan.wizardData[element] !== null
                    ) {
                        if (element === 'customFields') {
                            // Create a Set of used field names (excluding 'Length') for efficient lookup
                            const usedFieldNames = new Set(
                                testPlan.wizardData.customFields
                                    .filter(field => field.name !== 'Length')
                                    .map(field => field.name)
                            );

                            // Filter the object directly without array conversion
                            const filteredFields = {};
                            Object.entries(this.state.allCustomFields).forEach(([key, field]) => {
                                if (!usedFieldNames.has(field.name)) {
                                    filteredFields[key] = field;
                                }
                            });

                            newState.allCustomFields = filteredFields;
                        } else if (element === 'assembly') {
                            if (!testPlan.wizardData.assembly.hasOwnProperty('referenceOrder')) {
                                testPlan.wizardData.assembly.referenceOrder = 'Channel';
                            }
                        }

                        newState[element] = testPlan.wizardData[element];
                    }
                }

                if (newState.label) {
                    try {
                        Api.getLabelPdf(newState.label).then((x) => {
                            this.setState({ tempUrl: x });
                        }).catch(error => {
                            console.error(error);
                        });
                    } catch (e) {
                        console.error(e);
                    }
                }

                this.setState(newState, () => {
                    this.setState({ preventDefaults: false });
                });
            }
        });
    }

    async checkTestPlanName(name?: string) {
        try {
            let nameCheck = name || this.state.name;

            const x = await Api.getTestPlanByName(nameCheck);

            return x;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    updateStateWithKey = <K extends keyof WizardState>(
        key: K,
        data: Partial<WizardState[K]> | ((current: WizardState[K]) => Partial<WizardState[K]>),
        callback?: () => void
    ) => {
        this.setState(
            (state) => ({
                [key]: {
                    ...(state[key] as object),
                    ...(typeof data === 'function' ? data(state[key]) : data)
                }
            } as Pick<WizardState, K>),
            callback
        );
    };

    updateEquipment = (data, callback) => this.updateStateWithKey('equipment', data, callback);
    updateILCompensation = (data, callback) => this.updateStateWithKey('ilComp', data, callback);
    updateAssembly = (data, callback) => this.updateStateWithKey('assembly', data, callback);

    updateWizardData = (data, callback) => {
        this.setState(data, callback);
    };

    render() {
        if (this.state.loading) {
            return <div></div>;
        }

        return (
            <div id="wizard">
                {!this.state.saveTestPlanClicked && (
                    <Prompt
                        when={true}
                        message={(location) => {
                            return `You're about to leave the test plan builder page without saving the latest changes.\n Please confirm. `;
                        }}
                    />
                )}

                <Modal
                    title="Incompatible Test Plan Changes"
                    open={this.state.criticalSettingsChanged && this.state.saveTestPlanClicked}
                    closable={false}
                    footer={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Input
                                key="name"
                                style={{ flex: 1, marginRight: '0.5em' }}
                                value={this.state.tempName}
                                onChange={(e) => this.setState({ tempName: e.target.value })}
                            />
                            <Button
                                key="save"
                                type="primary"
                                onClick={async () => {
                                    this.setState((state) => ({
                                        forceCopy: true,
                                        name: state.tempName
                                    }), () => {
                                        this.saveTestPlan();
                                    });
                                }}
                            >
                                Save As...
                            </Button>
                            <Button
                                key="continue"
                                type="primary"
                                onClick={async() => this.setState({ forceOverwrite: true }, () => this.saveTestPlan())}
                                danger
                            >
                                    Overwrite
                            </Button>
                            <Button
                                key="cancel"
                                onClick={() => this.setState({ criticalSettingsChanged: false, saveTestPlanClicked: false })}
                            >
                                Cancel
                            </Button>
                        </div>
                    }
                >
                    <p>
                        This test plan has been significantly modified. Results saved with a previous version will no longer reload correctly.
                    </p>
                    <p>
                        Would you like to overwrite the current Test Plan or save as a new Test Plan with current changes?
                    </p>
                    {
                        this.state.criticalChanges && this.state.criticalChanges.length > 0 ? (
                            <div>
                                <h4>
                                    Differences between the current Test Plan and the new Test Plan:
                                </h4>
                                <Collapse>
                                    {this.state.criticalChanges.map((change, index) => (
                                        <CollapsePanel header={change.setting} key={index}>
                                            <p>Original: {change.original}</p>
                                            <p>Updated: {change.current}</p>
                                        </CollapsePanel>
                                    ))}
                                </Collapse>
                            </div>
                        ) : null
                    }
                </Modal>

                <Tabs
                    centered
                    className={
                        (!this.state.validation.dutConfigPage && this.state.page === PAGE__DUT_CONFIG) ||
                        (!this.state.validation.setupPage && this.state.page === PAGE__SETUP) ||
                        (!this.state.validation.measurementPage && this.state.page === PAGE__IL_RL) ||
                        (!this.state.validation.labelPage && this.state.page === PAGE__LABEL)
                            ? 'redx-ant-tabs'
                            : undefined
                    }
                    tabBarExtraContent={{ left: <div className='header'><Cable testPlanTemplate={this.state.testPlanTemplate} coreSize={this.state.assembly.coreSize} /></div>}}
                    defaultActiveKey={PAGE__DUT_CONFIG.toString()}
                    onChange={(key) => {
                        this.setState({ page: parseInt(key) });
                    }}
                >
                    <TabPane               
                        tab={
                            <div
                                data-testid="dut-config-page"
                                className={
                                    this.state.validation.dutConfigPage
                                        ? 'titlex'
                                        : this.state.page === PAGE__DUT_CONFIG
                                        ? 'titlex redx redx-pulse'
                                        : 'titlex redx'
                                }
                            >
                                DUT Config
                            </div>
                        }
                        key={PAGE__DUT_CONFIG}
                    >
                        <div className="wizard-card">
                            <DUTConfigPage
                                testPlanTemplate={this.state.testPlanTemplate}
                                wizard={this.state}
                                updateAssembly={this.updateAssembly}
                                updateWizardData={this.updateWizardData}
                                location={this.props.location}
                            />
                        </div>
                    </TabPane>

                    <TabPane
                        tab={<div className={this.state.page === PAGE__DUT_CONFIG ? 'iconx fade-in-out' : 'iconx'}>&gt;</div>}
                        disabled={true}
                        key={10}
                    />
                    
                    <TabPane
                        tab={
                            <div
                                data-testid="setup-page"
                                className={
                                    this.state.validation.setupPage
                                        ? 'titlex'
                                        : this.state.page === PAGE__SETUP
                                        ? 'titlex redx redx-pulse'
                                        : 'titlex redx'
                                }
                            >
                                Setup
                            </div>
                        }
                        key={PAGE__SETUP}
                    >
                        <div className="wizard-card">
                            <SetupPage
                                wizard={this.state}
                                updateWizardData={this.updateWizardData}
                                updateAssembly={this.updateAssembly}
                                updateEquipment={this.updateEquipment}
                            />
                        </div>
                    </TabPane>

                    <TabPane
                        tab={<div className={this.state.page === PAGE__SETUP ? 'iconx fade-in-out' : 'iconx'}>&gt;</div>}
                        disabled={true}
                        key={12}
                    />

                    {/* Polarity,if included */}
                    {this.state.assembly.testPolarity && (
                        <>
                            <TabPane
                                tab={
                                    <div data-testid="polarity-page" className="titlex">
                                        Polarity
                                    </div>
                                }
                                key={PAGE__POLARITY}>
                                <div className="wizard-card">
                                    <PolarityPage
                                        wizard={this.state}
                                        updateWizardData={this.updateWizardData}
                                    />
                                </div>
                            </TabPane>
                            <TabPane
                                tab={<div className={this.state.page === PAGE__POLARITY ? 'iconx fade-in-out' : 'iconx'}>&gt;</div>}
                                disabled={true}
                                key={13}
                            />
                        </>
                    )}

                    {/* IL RL */}
                    {(this.state.assembly.insertionLossSideA ||
                        this.state.assembly.insertionLossSideB ||
                        this.state.assembly.returnLossSideA ||
                        this.state.assembly.returnLossSideB ||
                        this.state.assembly.returnLossTotal ||
                        this.state.assembly.insertionLossTotal) && (
                        <React.Fragment>
                            <TabPane
                                tab={
                                    <div
                                        data-testid="il-rl-page"
                                        className={
                                            this.state.validation.measurementPage
                                                ? 'titlex'
                                                : this.state.page === PAGE__IL_RL
                                                ? 'titlex redx redx-pulse'
                                                : 'titlex redx'
                                        }
                                    >
                                        {(this.state.assembly.insertionLossSideA ||
                                            this.state.assembly.insertionLossSideB ||
                                            this.state.assembly.insertionLossTotal) &&
                                        !this.state.assembly.returnLossSideA &&
                                        !this.state.assembly.returnLossSideB &&
                                        !this.state.assembly.returnLossTotal
                                            ? 'IL'
                                            : (this.state.assembly.returnLossSideA ||
                                                  this.state.assembly.returnLossSideB ||
                                                  this.state.assembly.returnLossTotal) &&
                                              !this.state.assembly.insertionLossSideA &&
                                              !this.state.assembly.insertionLossSideB &&
                                              !this.state.assembly.insertionLossTotal
                                            ? 'RL'
                                            : 'IL & RL'}
                                    </div>
                                }
                                key={PAGE__IL_RL}
                            >
                                <ILRLPage
                                    equipment={this.state.equipment}
                                    updateEquipment={this.updateEquipment}
                                    assembly={this.state.assembly}
                                    updateAssembly={this.updateAssembly}
                                    updateIlComp={this.updateILCompensation}
                                    wizard={this.state}
                                    updateWizardData={this.updateWizardData}
                                />
                            </TabPane>
                            <TabPane
                                tab={<div className={this.state.page === PAGE__IL_RL ? 'iconx fade-in-out' : 'iconx'}>&gt;</div>}
                                disabled={true}
                                key={14}
                            />
                        </React.Fragment>
                    )}

                    <TabPane 
                        tab={<div data-testid="test-settings-page" className="titlex">Test Settings</div>}
                        key={PAGE__TEST_SETTINGS}>
                        <div className="wizard-card">
                            <TestSettingsPage
                                wizard={this.state}
                                updateWizardData={this.updateWizardData}
                                assembly={this.state.assembly}
                                updateAssembly={this.updateAssembly}
                                equipment={this.state.equipment}
                                updateEquipment={this.updateEquipment}
                            />
                        </div>
                    </TabPane>

                    <TabPane
                        tab={<div className={this.state.page === PAGE__TEST_SETTINGS ? 'iconx fade-in-out' : 'iconx'}>&gt;</div>}
                        disabled={true}
                        key={16}
                    />

                    <TabPane
                        tab={
                            <div
                                data-testid="label-page"
                                className={
                                    this.state.validation.labelPage
                                        ? 'titlex'
                                        : this.state.page === PAGE__LABEL
                                        ? 'titlex redx redx-pulse'
                                        : 'titlex redx'
                                }
                            >
                                Label
                            </div>
                        }
                        key={PAGE__LABEL}
                    >
                        <div className="wizard-card">
                            <LabelPage
                                wizard={this.state}
                                updateWizardData={this.updateWizardData}
                            />
                        </div>
                    </TabPane>
                    <TabPane
                        tab={<div className={this.state.page === PAGE__LABEL ? 'iconx fade-in-out' : 'iconx'}>&gt;</div>}
                        disabled={true}
                        key={18}
                    />

                    {/* Review */}
                    <TabPane 
                        tab={<div data-testid="review-page" className="titlex">Review & Save</div>} 
                        key={PAGE__REVIEW}>
                        <div className="wizard-card">
                            <ReviewPage
                                wizard={this.state}
                                updateWizardData={this.updateWizardData}
                                location={this.props.location}
                                saveTestPlan={() => this.saveTestPlan()}
                            />
                        </div>
                    </TabPane>
                </Tabs>
            </div>
        );
    }
}

export default withRouter(Wizard);
